




















































import { computed, defineComponent, ref } from "@vue/composition-api";
import { ContentPriceSubject } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { EventPostStatus } from "@scrile/api-provider/dist/projects/scrileConnect/EventPostProvider";
import useOneTimePurchase from "@/hooks/useOneTimePurchase";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCost, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { PurchaseDetails } from "@/payments/types";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import FormatMoney from "@/components/FormatMoney";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import UserSubscribeModal from "../UserSubscribeModal";

export default defineComponent({
  name: "EventTicketPurchaseModal",
  components: { PaymentConfirmation, UserSubscribeModal, BaseButton, BaseConfirm, FormatMoney },
  props: {
    show: { type: Boolean, default: false },
    contentFeed: { type: Object as () => ContentFeed },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
  },

  setup(props, { emit }) {
    const openPaymentConfirm = ref(false);
    const purchaseProcessing = ref(false);
    const publicPaymentSystems = ref<PaymentSystemPublic[] | null>(null);

    providers.PaymentSystemsProvider.findAllPublic().then(r => (publicPaymentSystems.value = r));

    const oneTimeCost = computed(() => {
      if (props.contentFeed && props.contentFeed.details.event?.ticketPricePackageId === "0") return 0;
      const paymentPackage = props.paymentPackages.find(
        item => item.id === props.contentFeed?.details.event?.ticketPricePackageId
      );
      if (!paymentPackage) return 0;
      return getCost(paymentPackage);
    });

    const minimumSubscriptionLevelId = computed(() => props.contentFeed?.details.event?.minimumLevelId);

    const isOnlyPrice = computed(
      () =>
        props.contentFeed?.details.event?.minimumLevelId === "0" &&
        props.contentFeed?.details.event?.ticketPricePackageId !== "0"
    );

    const paymentSystemId = computed(
      () =>
        publicPaymentSystems.value?.find(publicSystem => publicSystem.currencies.includes(getMainCurrencyCode()))?.id
    );

    const isEventStarted = computed(
      () =>
        props.contentFeed?.details?.event?.status === EventPostStatus.READY_TO_START ||
        props.contentFeed?.details?.event?.status === EventPostStatus.STARTED
    );

    const data = computed<ContentPriceSubject>(() => ({
      subjectId: props.contentFeed?.id ?? "0",
      subjectType: ContentPriceSubjectTypeEnum.EVENT,
    }));

    const purchaseDetails = computed<PurchaseDetails>(() => ({
      pricePackageId: props.contentFeed?.details.event?.ticketPricePackageId ?? "0",
      price: formatMoneyString(oneTimeCost.value, { currencyCode: getMainCurrencyCode() }) || undefined,
      subject: {
        subjectId: props.contentFeed?.id ?? "0",
        subjectType: PurchaseSubjectTypeEnum.EVENT,
      },
    }));

    const { performOneTimePurchase, purchaseInfoDetails } = useOneTimePurchase(data, purchaseDetails);

    const handleOneTimePurchase = async () => {
      try {
        if (!props.contentFeed || !paymentSystemId) return;
        purchaseProcessing.value = true;
        const response = await performOneTimePurchase();

        emit(EVENTS.CONTENT_FEED_PURCHASED, { userId: props.contentFeed.userId, feedId: props.contentFeed.id });
        if (response.success) {
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("One time buy {0} content for {1}", [
              props.contentFeed?.user.screenName,
              oneTimeCost.value ? formatMoneyString(oneTimeCost.value, { currencyCode: getMainCurrencyCode() }) : "0",
            ]),
            image: {
              user: props.contentFeed?.user,
            },
            button: {
              text: $t("Close"),
              cb: () => {},
            },
            position: "top",
          });
          emit("book");
        }
      } finally {
        purchaseProcessing.value = false;
      }
    };

    const onClose = () => {
      if (purchaseProcessing.value) return;
      if (isOnlyPrice.value) {
        emit("close");
      }
      openPaymentConfirm.value = false;
    };

    return {
      oneTimeCost,
      isOnlyPrice,
      purchaseProcessing,
      minimumSubscriptionLevelId,
      isEventStarted,
      purchaseInfoDetails,
      openPaymentConfirm,
      handleOneTimePurchase,
      onClose,
    };
  },
});
